import React from "react";
import { useNavigate } from "@remix-run/react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@core/api";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { ClientOnly } from "@core/components/client-only";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import type { PatientModel } from "@/apidtos";
import { useSelectedPatient } from "@/overrides/med/components/SelectedPatientProvider";

const isOfficePatient = (p?: PatientModel | null) =>
  !!p?.Key?.startsWith("in-office-use");

const labelFor = (p: PatientModel) =>
  isOfficePatient(p) ? "In-Office Use" : (p.Name?.trim() || `Patient #${p.Id}`);

const HeaderOrderingFor: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { patientId, patient, setPatientId, setPatients } = useSelectedPatient();
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);

  // Still load the provider patient list so we can resolve In-Office eligibility,
  // hydrate the selected patient label, and share the list with cart consumers.
  const { data, isLoading } = useQuery({
    queryKey: ["header", "ordering-for", "patients"],
    queryFn: () => api.ListProviderPatients({ PageSize: 100 }),
    staleTime: 60_000,
    enabled: !!user,
  });

  const patients: PatientModel[] = React.useMemo(
    () => data?.Results ?? [],
    [data],
  );

  const inOfficePatient = React.useMemo(
    () => patients.find((p) => isOfficePatient(p)) ?? null,
    [patients],
  );

  React.useEffect(() => {
    setPatients(patients);
  }, [patients, setPatients]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Prefer the hydrated patient object; fall back to the loaded list so we never
  // flash "Patient #N" for In-Office Use while localStorage id is set.
  const selectedPatient =
    patient
    ?? (patientId != null ? patients.find((p) => p.Id === patientId) : undefined)
    ?? null;

  const selectedLabel = selectedPatient ? labelFor(selectedPatient) : null;
  const hasSelection = !!patientId;
  const inOfficeSelected =
    hasSelection && selectedPatient != null && isOfficePatient(selectedPatient);

  return (
    <div className="header-patient-info">
      <span className="header-ordering-label">Ordering for</span>
      <div className="header-patient-selector" ref={wrapRef}>
        <button
          type="button"
          className={`header-patient-btn${hasSelection ? " has-patient" : ""}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {isLoading ? (
            <span className="header-patient-placeholder">Loading…</span>
          ) : selectedLabel ? (
            <span className="header-patient-selected-name">{selectedLabel}</span>
          ) : (
            <span className="header-patient-placeholder">Select patient</span>
          )}
          <svg
            className="header-patient-chevron"
            viewBox="0 0 12 12"
            width="10"
            height="10"
          >
            <path
              d="M3 5l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
        <div className={`header-patient-dropdown${open ? " open" : ""}`} role="listbox">
          {inOfficePatient && (
            <button
              type="button"
              className={[
                "header-patient-option",
                "header-patient-option-office",
                inOfficeSelected ? "header-patient-option-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => {
                setPatientId(inOfficePatient.Id);
                setOpen(false);
              }}
            >
              In-Office Use
            </button>
          )}
          <button
            type="button"
            className="header-patient-option"
            onClick={() => {
              setOpen(false);
              navigate("/dashboard/patients");
            }}
          >
            Patient Specific Order
          </button>
          {hasSelection && (
            <button
              type="button"
              className="header-patient-option header-patient-option-clear"
              onClick={() => {
                setPatientId(null);
                setOpen(false);
              }}
            >
              Clear Selection
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const HeaderOrderingForExtension: React.FC = () => (
  <ClientOnly>
    <HeaderOrderingFor />
  </ClientOnly>
);

registerExtension(
  "_site.header.beforeMiniMenu",
  HeaderOrderingForExtension,
  "HeaderOrderingForExtension",
);
